/**
 * Places the read-only current-squad overlay onto the watchlist Pitch.
 *
 * A squad player lands in the first natural position the current formation
 * renders, and each slot reads highest GPR first (SE-8668); a player who is
 * also a list member renders once, in the list's slot with the list's rank,
 * and is reported as `inList` instead of being placed a second time
 * (PRD-4.7, D14–D16).
 */
import type {
  ScoutingGetWatchlistPitchQuery,
  TacticalSlot,
} from "@/generated/graphql";

/** Authorized Pitch query payload. */
type Pitch = NonNullable<ScoutingGetWatchlistPitchQuery["getWatchlistPitch"]>;
/** One current-squad overlay player. */
export type SquadOverlayPlayer = Pitch["overlay"][number];

/** Squad players placed per rendered slot plus the overlap bookkeeping. */
export interface SquadOverlayProjection {
  readonly squadBySlot: Partial<
    Record<TacticalSlot, readonly SquadOverlayPlayer[]>
  >;
  /** Squad players who are also list members; rendered once via the list. */
  readonly inListIds: ReadonlySet<string>;
  /** Squad players with no natural position in the current formation. */
  readonly unplaced: readonly SquadOverlayPlayer[];
}

/** Empty projection used while the overlay is switched off. */
export const EMPTY_SQUAD_OVERLAY: SquadOverlayProjection = {
  squadBySlot: {},
  inListIds: new Set<string>(),
  unplaced: [],
};

const firstRenderedSlot = (
  player: SquadOverlayPlayer,
  rendered: ReadonlySet<string>
): TacticalSlot | undefined =>
  player.naturalPositions
    .map(position => position.abbreviation ?? "")
    .find(code => rendered.has(code)) as TacticalSlot | undefined;

/**
 * Orders one slot's squad players for its card (SE-8668): GPR descending,
 * unrated last. A 0 is a rating, not a missing one. Equal ratings keep the
 * API order (`Array.prototype.sort` is stable).
 * @param players - Squad players placed in one slot.
 * @returns New array, highest GPR first.
 */
const bySlotOrder = (
  players: readonly SquadOverlayPlayer[]
): readonly SquadOverlayPlayer[] =>
  [...players].sort((left, right) => {
    if (typeof left.gpr !== "number") {
      return typeof right.gpr !== "number" ? 0 : 1;
    }
    if (typeof right.gpr !== "number") return -1;
    return right.gpr - left.gpr;
  });

/**
 * Projects squad players onto the slots of the current formation.
 * @param overlay - Current-squad players from the Pitch read.
 * @param memberIds - Player identities of the loaded list members.
 * @param slots - Complete slot list of the current formation.
 * @returns Slot placements, overlap identities and unplaceable players.
 */
export const projectSquadOverlay = (
  overlay: readonly SquadOverlayPlayer[],
  memberIds: ReadonlySet<string>,
  slots: readonly TacticalSlot[]
): SquadOverlayProjection => {
  const rendered: ReadonlySet<string> = new Set(slots);
  const placed = overlay.reduce<SquadOverlayProjection>(
    (projection, player) => {
      if (memberIds.has(player.id)) {
        return {
          ...projection,
          inListIds: new Set([...projection.inListIds, player.id]),
        };
      }
      const slot = firstRenderedSlot(player, rendered);
      if (!slot) {
        return { ...projection, unplaced: [...projection.unplaced, player] };
      }
      return {
        ...projection,
        squadBySlot: {
          ...projection.squadBySlot,
          [slot]: [...(projection.squadBySlot[slot] ?? []), player],
        },
      };
    },
    EMPTY_SQUAD_OVERLAY
  );
  return {
    ...placed,
    squadBySlot: Object.fromEntries(
      Object.entries(placed.squadBySlot).map(([slot, players]) => [
        slot,
        bySlotOrder(players),
      ])
    ),
  };
};

/**
 * Sorts squad players for the tier sections: ascending GPR, unrated last.
 * @param players - Squad players of one tier.
 * @returns New array in display order.
 */
export const sortSquadTier = (
  players: readonly SquadOverlayPlayer[]
): readonly SquadOverlayPlayer[] =>
  [...players].sort((left, right) => {
    if (typeof left.gpr !== "number") {
      return typeof right.gpr !== "number" ? 0 : 1;
    }
    if (typeof right.gpr !== "number") return -1;
    return left.gpr - right.gpr;
  });

/**
 * Whether the first-team scroll fade should render (SE-8486, W69): true
 * while the list is taller than its box AND is not scrolled to its end — the
 * fade says "more players this way" and goes once the last row is in view.
 * The vertical twin of the pill bar's `computeShowEndFade`, kept here so the
 * panel does not pull the squad-planning organism into scouting.
 * @param contentHeight - Total scrollable content height.
 * @param layoutHeight - Visible scroller height.
 * @param scrollY - Current vertical scroll offset.
 * @returns Whether the bottom fade is visible.
 */
export const computeShowSquadFade = (
  contentHeight: number,
  layoutHeight: number,
  scrollY: number
): boolean => {
  const hasOverflow = contentHeight > layoutHeight + 1;
  const isAtEnd = scrollY >= contentHeight - layoutHeight - 1;
  return hasOverflow && !isAtEnd;
};
